import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PULSE_TESTIDS, HERO_IMAGES } from "@/lib/pulseConstants";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";

export default function HomeHero() {
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const media = window.matchMedia("(prefers-reduced-motion: reduce)");
        if (media.matches) return;
        let raf = null;
        function onScroll() {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                setOffset(Math.min(window.scrollY * 0.08, 40));
                raf = null;
            });
        }
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    function scrollToSelectedWork(e) {
        e.preventDefault();
        const target = document.getElementById("selected-work");
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    return (
        <section
            data-testid={PULSE_TESTIDS.hero.section}
            className="relative overflow-hidden pt-24 md:pt-28 lg:pt-32 pb-2 md:pb-20 min-h-[92vh] flex items-center"
        >
            {/* Background image + parallax */}
            <div className="absolute inset-0 -z-10">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${HERO_IMAGES[0]})`,
                        transform: `translateY(${offset}px) scale(1.06)`,
                    }}
                />
                <div className="absolute inset-0 hero-radial" />
                <div className="absolute inset-0 bg-[#07070A]/40" />
            </div>

            {/* Faint grid */}
            <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" aria-hidden="true" />

            <div className="container-editorial relative z-10 w-full">
                <div className="max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 mb-6"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-[#FF2A2A] animate-pulse-glow" />
                        <span className="text-[11px] font-mono uppercase tracking-[0.24em] text-white/80">
                            The culture-led experiential agency
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                        className="display-1 text-white"
                    >
                        Where brands
                        <br />
                        <span className="text-[#FF2A2A]">become culture.</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="body-lg mt-8 md:mt-10 max-w-2xl"
                    >
                        We transform marketing campaigns into culture-led experiences that people choose to attend, capture and share.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-10 flex flex-col sm:flex-row gap-4"
                    >
                        <Button
                            asChild
                            className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-12 md:h-14 px-7 text-base"
                            data-testid={PULSE_TESTIDS.hero.primaryCta}
                        >
                            <Link to="/contact" onClick={handlePulseBriefClick} className="inline-flex items-center gap-2">
                                Start Your Pulse Brief
                                <ArrowUpRight size={18} />
                            </Link>
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            className="rounded-full border-white/25 bg-white/5 hover:bg-white/10 text-white h-12 md:h-14 px-7 text-base"
                            data-testid={PULSE_TESTIDS.hero.secondaryCta}
                        >
                            <a
                                href="#selected-work"
                                onClick={scrollToSelectedWork}
                                className="inline-flex items-center gap-2"
                            >
                                View Selected Work
                                <ChevronDown size={16} />
                            </a>
                        </Button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.85 }}
                        className="mt-14 flex items-center gap-6 text-xs font-mono text-white/50 uppercase tracking-widest"
                    >
                        <div className="inline-flex items-center gap-2">
                            <ArrowDown size={12} /> Scroll to explore
                        </div>
                        <div className="hidden sm:flex items-center gap-6">
                            <span>Kopitiam takeovers</span>
                            <span className="h-3 w-px bg-white/15" />
                            <span>Pop-up worlds</span>
                            <span className="h-3 w-px bg-white/15" />
                            <span>Creator nights</span>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
